import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export interface SciHeadOpts {
  basePath: string;
  title: string;
  description: string;
  toolName: string;
  faqs?: { q: string; a: string }[];
}

/**
 * Standard `head()` for a science tool route: title/description/OG meta,
 * hreflang links, and BreadcrumbList (+ optional FAQPage) JSON-LD.
 */
export function sciHead(opts: SciHeadOpts) {
  const scripts = [
    {
      type: "application/ld+json",
      // Breadcrumb URLs are locale-aware so /de/… pages don't point their
      // structured data at the English URLs (contradicting their canonical).
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        inLanguage: getLocale(),
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: m.sci_breadcrumb_home(),
            item: localeUrl("/"),
          },
          {
            "@type": "ListItem",
            position: 2,
            name: opts.toolName,
            item: localeUrl(opts.basePath),
          },
        ],
      }),
    },
  ];

  if (opts.faqs && opts.faqs.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: opts.faqs.map(({ q, a }) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      }),
    });
  }

  return {
    meta: [
      { title: opts.title },
      { name: "description", content: opts.description },
      { property: "og:title", content: opts.title },
      { property: "og:description", content: opts.description },
      ...i18nOgMeta(opts.basePath),
    ],
    links: [...i18nLinks(opts.basePath)],
    scripts,
  };
}
